import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, differenceInDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  CalendarDays, Users, Sparkles, CreditCard, Check, AlertCircle, Loader2,
  ChevronLeft, ChevronRight, Star, Quote, Wifi, Dog, Waves, Flame, Music, Car, Train, Bed, Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRICE_PER_NIGHT = 800;
const CLEANING_FEE = 300;
const MIN_NIGHTS = 2;
const MAX_GUESTS = 12;

// Villa images for carousel - sans les projections d'événements
const villaImages = [
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/eef65rl5_06526C50-55D0-4775-AEFE-82A869B5EAF6.PNG", alt: "Vue aérienne" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/qn91zz8c_piscinenormont.png", alt: "Piscine" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/lqspir0s_soiree.png", alt: "Villa de nuit" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/1fxxzk5s_IMG_4445.jpg", alt: "Façade" },
  // Salons
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/680xupg6_IMG_4505.jpg", alt: "Salon" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/d0j6d27y_IMG_4454.jpg", alt: "Salon vue jardin" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/l5p0vc54_IMG_4457.JPG", alt: "Bibliothèque cheminée" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/dinkeggh_IMG_4506.jpg", alt: "Cuisine" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/ogu8caf6_IMG_4470.JPG", alt: "Salle à manger" },
  // Espaces de vie supplémentaires
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/kwi0ehu0_a4b4ad24-786b-4e72-896a-3f479067c828.JPG", alt: "Salle à manger avec lustre" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/c9c8yuzg_c7d19d56-2805-43dd-bad0-d85029e3cbb2.JPG", alt: "Petit salon avec TV" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/spvsyc0r_9d15e18b-ff81-4c11-bc84-399bbda81808.JPG", alt: "Meuble TV et DVD" },
  // Chambres
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/94wmg6lb_IMG_4472.JPG", alt: "Chambre 2 lits" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/cr2qofps_IMG_4511.jpg", alt: "Chambre dorée" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/r03onwk3_IMG_4512.jpg", alt: "Chambre lumineuse" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/0zni7kb2_IMG_4514.jpg", alt: "Chambre cosy" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/q7klr1r6_5f9cbf39-33bd-404d-aca2-5bc239752651.JPG", alt: "Chambre avec terrasse" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/rkbuxeu4_46681970-afdc-466f-81fd-f2db7812b2f0.JPG", alt: "Grande chambre élégante" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/3cfybi8r_b8b78b5e-a635-4100-adcc-dc6747f822a2.JPG", alt: "Chambre avec salle de bain" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/qchtroef_b6206408-85c7-41b6-ae58-183cf9fd8bab.JPG", alt: "Chambre au miroir doré" },
  // Salles de bain
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/qrjmudrw_IMG_4521.jpg", alt: "Salle de bain" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/83pdrwx7_IMG_4516.jpg", alt: "Double vasque" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/pkr7h4ig_IMG_4517.jpg", alt: "Baignoire" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/rxzjnzx9_IMG_4516.jpg", alt: "Salle de bain mosaïque" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/bnd7jwvb_IMG_4526.jpg", alt: "SDB rangements" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/6wihul0s_IMG_4519.jpg", alt: "Salle de bain rouge" },
  // Extérieur & Parc
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/kqxy7bbh_IMG_4465.jpg", alt: "Jardin escalier" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/6edms9f4_jardin.jpg", alt: "Grand jardin" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/9ukxucu9_F6E1600D-935C-42C7-AB21-55B3C73F2FD7.PNG", alt: "Forêt" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/eqj1fuil_cb1bbce0-1cc1-451c-8a45-73445f4ff570.JPG", alt: "Terrasse avec vue" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/1ve6e0e8_IMG_4424.jpg", alt: "Parc arboré" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/jsbboy0f_IMG_4425.jpg", alt: "Piscine vue 2" },
  { url: "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/gzsj63fs_IMG_4433.jpg", alt: "Paillote" },
];

// Amenities recap
const amenities = [
  { icon: Wifi, label: "Wi-Fi haut débit" },
  { icon: Dog, label: "Animaux acceptés" },
  { icon: Waves, label: "Piscine" },
  { icon: Flame, label: "Cheminée" },
  { icon: Music, label: "Piano" },
  { icon: Car, label: "Parking gratuit" },
  { icon: Train, label: "Gare à 2 km" },
  { icon: Bed, label: "7 chambres" },
  { icon: Home, label: "Domaine 7 hectares" },
];

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState("2");
  const [cleaningOption, setCleaningOption] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [specialRequests, setSpecialRequests] = useState("");
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 5.0, total_reviews: 0 });
  const [expandedReviews, setExpandedReviews] = useState({});
  
  // Site settings (pool notice, etc.)
  const [siteSettings, setSiteSettings] = useState({ pool_under_construction: true });
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  
  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % villaImages.length);
  };
  
  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev - 1 + villaImages.length) % villaImages.length);
  };

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/reviews`),
          axios.get(`${API_URL}/api/reviews/stats`)
        ]);
        setReviews(reviewsRes.data);
        setReviewStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings/public`);
        setSiteSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Restore pending booking from localStorage (after login redirect)
  useEffect(() => {
    const pendingBookingStr = localStorage.getItem('pendingBooking');
    if (pendingBookingStr && user) {
      try {
        const booking = JSON.parse(pendingBookingStr);
        
        // Check if the pending booking is not expired (30 minutes max)
        const thirtyMinutes = 30 * 60 * 1000;
        if (booking.timestamp && (Date.now() - booking.timestamp) > thirtyMinutes) {
          // Expired, remove it
          localStorage.removeItem('pendingBooking');
          return;
        }
        
        // Restore the dates
        const checkIn = new Date(booking.check_in);
        const checkOut = new Date(booking.check_out);
        setDateRange({ from: checkIn, to: checkOut });
        setGuests(booking.guests || "2");
        setCleaningOption(booking.cleaningOption || false);
        
        // Clear the pending booking from localStorage
        localStorage.removeItem('pendingBooking');
        
        toast.success('Vos dates ont été restaurées. Vous pouvez finaliser votre réservation.');
      } catch (error) {
        console.error('Error restoring pending booking:', error);
        localStorage.removeItem('pendingBooking');
      }
    }
  }, [user]);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Paiement annulé');
    }
  }, [searchParams]);

  // Fetch availability
  useEffect(() => {
    fetchAvailability();
  }, []);

  // Auto-scroll images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % villaImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/availability`);
      const dates = response.data.unavailable_dates.map(d => new Date(d));
      setUnavailableDates(dates);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Impossible de vérifier le statut du paiement');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        toast.success('Paiement réussi ! Votre réservation est confirmée.');
      } else if (response.data.status === 'expired') {
        toast.error('Session de paiement expirée');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  // Calculate price
  const priceCalculation = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return null;
    
    const nights = differenceInDays(dateRange.to, dateRange.from);
    if (nights < MIN_NIGHTS) return null;
    
    const basePrice = nights * PRICE_PER_NIGHT;
    const cleaning = cleaningOption ? CLEANING_FEE : 0;
    const discountAmount = promoApplied ? basePrice * (discount / 100) : 0;
    const total = basePrice + cleaning - discountAmount;
    
    return {
      nights,
      basePrice,
      cleaning,
      discount: discountAmount,
      total
    };
  }, [dateRange, cleaningOption, promoApplied, discount]);

  // Check if date is disabled
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    return unavailableDates.some(d => isSameDay(d, date));
  };

  // Apply promo code
  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    try {
      if (promoCode.toUpperCase() === 'WELCOME10') {
        setDiscount(10);
        setPromoApplied(true);
        toast.success('Code promo appliqué : -10%');
      } else {
        toast.error('Code promo invalide');
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification du code');
    }
  };

  // Create booking
  const handleBooking = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error('Veuillez sélectionner vos dates');
      return;
    }

    if (!user) {
      // Save booking details to localStorage before redirecting to login
      // Include timestamp to expire after 30 minutes
      const pendingBooking = {
        check_in: format(dateRange.from, 'yyyy-MM-dd'),
        check_out: format(dateRange.to, 'yyyy-MM-dd'),
        guests: guests,
        cleaningOption: cleaningOption,
        timestamp: Date.now()
      };
      localStorage.setItem('pendingBooking', JSON.stringify(pendingBooking));
      
      toast.info('Veuillez vous connecter pour réserver');
      navigate('/login', { state: { from: '/booking', pendingBooking: true } });
      return;
    }

    setLoading(true);
    try {
      const bookingResponse = await axios.post(`${API_URL}/api/bookings/create`, {
        check_in: format(dateRange.from, 'yyyy-MM-dd'),
        check_out: format(dateRange.to, 'yyyy-MM-dd'),
        guests: parseInt(guests),
        cleaning_option: cleaningOption,
        promo_code: promoApplied ? promoCode : null,
        special_requests: specialRequests
      });

      const newBookingId = bookingResponse.data.booking_id;

      const checkoutResponse = await axios.post(
        `${API_URL}/api/payments/create-checkout?booking_id=${newBookingId}`,
        {}
      );

      window.location.href = checkoutResponse.data.checkout_url;
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#FAFAFA]" data-testid="payment-success">
        <div className="max-w-md mx-auto text-center p-12 bg-white border border-[#E5E5E5]">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-['Playfair_Display'] mb-4">Réservation confirmée !</h1>
          <p className="text-[#666666] mb-8 font-['Outfit']">
            Vous recevrez un email de confirmation avec tous les détails de votre séjour.
          </p>
          <Button 
            onClick={() => navigate('/client')}
            className="bg-[#C5A059] hover:bg-[#A6864A] text-white w-full"
            data-testid="go-to-dashboard"
          >
            Voir mes réservations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA]" data-testid="booking-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] mb-4">Réservez votre séjour</h1>
          <p className="text-white/70 font-['Outfit']">Sélectionnez vos dates et le nombre d'invités</p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Calendar & Options */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Villa Photos Carousel */}
            <div className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid="villa-photos">
              <div className="relative h-[300px]">
                <img 
                  src={villaImages[currentImageIndex].url} 
                  alt={villaImages[currentImageIndex].alt}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => openLightbox(currentImageIndex)}
                />
                <div className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1 text-sm font-['Outfit']">
                  {villaImages[currentImageIndex].alt}
                </div>
                {/* View full size button */}
                <button 
                  onClick={() => openLightbox(currentImageIndex)}
                  className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white px-4 py-2 text-sm font-['Outfit'] flex items-center gap-2 transition-colors"
                  data-testid="view-fullsize-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                  Voir en grand
                </button>
                <button 
                  onClick={() => setCurrentImageIndex((prev) => (prev - 1 + villaImages.length) % villaImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex((prev) => (prev + 1) % villaImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white flex items-center justify-center"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 right-4 flex gap-1">
                  {villaImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full ${index === currentImageIndex ? 'bg-[#C5A059]' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Amenities Recap */}
            <div className="bg-white border border-[#E5E5E5] p-6" data-testid="amenities-recap">
              <h3 className="text-lg font-['Playfair_Display'] mb-4">Ce qui vous attend</h3>
              <div className="grid grid-cols-3 gap-4">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-[#666666] font-['Outfit']">
                    <amenity.icon className="w-4 h-4 text-[#C5A059]" />
                    <span>{amenity.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white border border-[#E5E5E5] p-6" data-testid="calendar-section">
              <div className="flex items-center gap-2 mb-6">
                <CalendarDays className="w-5 h-5 text-[#C5A059]" />
                <h2 className="text-xl font-['Playfair_Display']">Sélectionnez vos dates</h2>
              </div>
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
                disabled={isDateDisabled}
                locale={fr}
                fromDate={new Date()}
                toDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                className="w-full"
                data-testid="booking-calendar"
              />
              {dateRange.from && dateRange.to && differenceInDays(dateRange.to, dateRange.from) < MIN_NIGHTS && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-['Outfit']">Minimum {MIN_NIGHTS} nuits requis</span>
                </div>
              )}
            </div>

            {/* Options */}
            <div className="bg-white border border-[#E5E5E5] p-6" data-testid="options-section">
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-[#C5A059]" />
                <h2 className="text-xl font-['Playfair_Display']">Détails du séjour</h2>
              </div>
              
              {/* Pool notice - always visible */}
              {siteSettings.pool_under_construction && (
                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded text-sm" data-testid="pool-notice-details">
                  <p className="text-amber-800 font-['Outfit']">
                    <strong>ℹ️ Information :</strong> La piscine est actuellement en travaux et sera bientôt disponible.
                  </p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="font-['Outfit'] mb-2 block">Nombre d'invités</Label>
                  <Select value={guests} onValueChange={setGuests} data-testid="guests-select">
                    <SelectTrigger>
                      <SelectValue placeholder="Nombre d'invités" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(MAX_GUESTS)].map((_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          {i + 1} {i === 0 ? 'personne' : 'personnes'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-[#666666] mt-2 font-['Outfit']">
                    Plus de 12 personnes ? <a href="/events" className="text-[#C5A059] hover:underline">Demandez un devis</a>
                  </p>
                </div>

                <div>
                  <Label className="font-['Outfit'] mb-2 block">Code promo</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="Entrez votre code"
                      disabled={promoApplied}
                      className="flex-1"
                      data-testid="promo-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={applyPromoCode}
                      disabled={promoApplied}
                      data-testid="apply-promo"
                    >
                      {promoApplied ? <Check className="w-4 h-4" /> : 'Appliquer'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <Checkbox 
                  id="cleaning"
                  checked={cleaningOption}
                  onCheckedChange={setCleaningOption}
                  data-testid="cleaning-checkbox"
                />
                <Label htmlFor="cleaning" className="font-['Outfit'] cursor-pointer">
                  Option ménage (+{CLEANING_FEE}€)
                </Label>
              </div>

              <div className="mt-6">
                <Label className="font-['Outfit'] mb-2 block">Demandes spéciales (optionnel)</Label>
                <textarea 
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  className="w-full border border-[#E5E5E5] p-4 h-24 resize-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
                  placeholder="Arrivée tardive, besoins spécifiques..."
                  data-testid="special-requests"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Price Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E5E5] p-6 sticky top-24" data-testid="price-summary">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-[#C5A059]" />
                <h2 className="text-xl font-['Playfair_Display']">Récapitulatif</h2>
              </div>
              
              {/* Rating summary */}
              {reviewStats.total_reviews > 0 && (
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[#E5E5E5]">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= Math.round(reviewStats.average_rating) ? 'fill-[#C5A059] text-[#C5A059]' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-sm font-['Outfit'] text-[#666666]">
                    {reviewStats.average_rating}/5 ({reviewStats.total_reviews} avis)
                  </span>
                </div>
              )}

              {priceCalculation ? (
                <>
                  {/* Pool notice */}
                  {siteSettings.pool_under_construction && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm" data-testid="pool-notice">
                      <p className="text-amber-800 font-['Outfit']">
                        <strong>ℹ️ Information :</strong> La piscine est actuellement en travaux et sera bientôt disponible.
                      </p>
                    </div>
                  )}
                  <div className="space-y-4 mb-6">
                    <div className="price-row">
                      <span className="text-[#666666] font-['Outfit']">
                        {PRICE_PER_NIGHT}€ x {priceCalculation.nights} nuits
                      </span>
                      <span className="font-['Outfit']">{priceCalculation.basePrice}€</span>
                    </div>
                    {priceCalculation.cleaning > 0 && (
                      <div className="price-row">
                        <span className="text-[#666666] font-['Outfit']">Option ménage</span>
                        <span className="font-['Outfit']">{priceCalculation.cleaning}€</span>
                      </div>
                    )}
                    {priceCalculation.discount > 0 && (
                      <div className="price-row text-green-600">
                        <span className="font-['Outfit']">Réduction ({discount}%)</span>
                        <span className="font-['Outfit']">-{priceCalculation.discount}€</span>
                      </div>
                    )}
                    <div className="price-row border-t-2 border-[#0A0A0A] pt-4">
                      <span className="text-lg font-['Outfit']">Total</span>
                      <span className="text-2xl text-[#C5A059] font-['Outfit']">{priceCalculation.total}€</span>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-[#FAFAFA] text-sm text-[#666666] font-['Outfit']">
                    <p className="mb-2"><strong>Dates :</strong> {format(dateRange.from, 'd MMMM', { locale: fr })} - {format(dateRange.to, 'd MMMM yyyy', { locale: fr })}</p>
                    <p><strong>Invités :</strong> {guests} {parseInt(guests) > 1 ? 'personnes' : 'personne'}</p>
                  </div>

                  <Button 
                    className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white py-6"
                    onClick={handleBooking}
                    disabled={loading}
                    data-testid="book-now-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...</>
                    ) : (
                      <>Réserver maintenant</>
                    )}
                  </Button>

                  <p className="text-xs text-center text-[#666666] mt-4 font-['Outfit']">
                    Paiement sécurisé par Stripe
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <Sparkles className="w-12 h-12 text-[#E5E5E5] mx-auto mb-4" />
                  <p className="text-[#666666] font-['Outfit']">
                    Sélectionnez vos dates pour voir le prix
                  </p>
                  <p className="text-2xl mt-4 font-['Playfair_Display']">
                    À partir de <span className="text-[#C5A059]">{PRICE_PER_NIGHT}€</span>
                  </p>
                  <p className="text-sm text-[#666666] font-['Outfit']">par nuit</p>
                </div>
              )}

              {/* Info */}
              <div className="mt-6 pt-6 border-t border-[#E5E5E5] space-y-2 text-sm text-[#666666] font-['Outfit']">
                <p>✓ Arrivée : 12h00</p>
                <p>✓ Départ : 12h00</p>
                <p>✓ Caution : 2000€ (empreinte bancaire)</p>
                <div className="pt-2 border-t border-[#E5E5E5] mt-2">
                  <p className="font-semibold text-[#0A0A0A] mb-1">Politique d'annulation :</p>
                  <p>• -14 jours avant : 50% retenus</p>
                  <p>• -7 jours avant : 100% retenus</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-16 bg-[#FAFAFA]" data-testid="reviews-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] mb-4">Avis de nos clients</h2>
              <div className="flex items-center justify-center gap-3">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      className={`w-6 h-6 ${star <= Math.round(reviewStats.average_rating) ? 'fill-[#C5A059] text-[#C5A059]' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <span className="text-lg font-['Outfit'] text-[#666666]">
                  {reviewStats.average_rating}/5 basé sur {reviewStats.total_reviews} avis vérifiés
                </span>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.slice(0, 3).map((review, index) => {
                const MAX_CHARS = 200;
                const isLongComment = review.comment && review.comment.length > MAX_CHARS;
                const isExpanded = expandedReviews[index];
                const displayComment = isLongComment && !isExpanded 
                  ? review.comment.substring(0, MAX_CHARS) + '...'
                  : review.comment;
                
                return (
                  <div 
                    key={index}
                    className="bg-white p-8 border border-[#E5E5E5] relative"
                    data-testid={`review-card-${index}`}
                  >
                    <Quote className="absolute top-4 right-4 w-8 h-8 text-[#C5A059]/20" />
                    
                    {/* Stars */}
                    <div className="flex mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-5 h-5 ${star <= (review.rating || 5) ? 'fill-[#C5A059] text-[#C5A059]' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                    
                    {/* Comment with "Lire la suite" */}
                    <div className="mb-6">
                      <p className="text-[#666666] font-['Outfit'] leading-relaxed italic">
                        "{displayComment}"
                      </p>
                      {isLongComment && (
                        <button
                          onClick={() => setExpandedReviews(prev => ({ ...prev, [index]: !isExpanded }))}
                          className="text-[#C5A059] hover:text-[#A6864A] text-sm font-['Outfit'] mt-2 underline transition-colors"
                          data-testid={`read-more-${index}`}
                        >
                          {isExpanded ? 'Voir moins' : 'Lire la suite'}
                        </button>
                      )}
                    </div>
                    
                    {/* Author */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold font-['Outfit']">{review.author}</p>
                        <p className="text-sm text-[#999] font-['Outfit']">
                          {review.source === 'airbnb' && 'Airbnb'}
                          {review.source === 'abritel' && 'Abritel'}
                          {review.source === 'client' && 'Client vérifié'}
                          {review.source === 'admin' && 'Avis vérifié'}
                        </p>
                      </div>
                      {review.source && (
                        <span className="text-xs text-[#C5A059] font-['Outfit'] bg-[#C5A059]/10 px-2 py-1 rounded">
                          Vérifié ✓
                        </span>
                      )}
                    </div>
                    
                    {/* Admin response */}
                    {review.admin_response && (
                      <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                        <p className="text-sm text-[#666666] font-['Outfit']">
                          <strong className="text-[#0A0A0A]">Réponse du propriétaire :</strong> {review.admin_response}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          {/* Close button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-[#C5A059] z-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Image counter */}
          <div className="absolute top-4 left-4 text-white font-['Outfit'] text-lg">
            {lightboxIndex + 1} / {villaImages.length}
          </div>
          
          {/* Main image */}
          <img 
            src={villaImages[lightboxIndex].url}
            alt={villaImages[lightboxIndex].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Image caption */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white font-['Outfit'] text-lg bg-black/50 px-4 py-2">
            {villaImages[lightboxIndex].alt}
          </div>
          
          {/* Previous button */}
          <button 
            onClick={(e) => { e.stopPropagation(); prevLightboxImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          {/* Next button */}
          <button 
            onClick={(e) => { e.stopPropagation(); nextLightboxImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          
          {/* Thumbnails */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto py-2">
            {villaImages.map((img, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(index); }}
                className={`flex-shrink-0 w-16 h-12 overflow-hidden border-2 transition-all ${
                  index === lightboxIndex ? 'border-[#C5A059]' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;
